/**
 * Select lists: where CSS can't style a select's open list (appearance: base-select, see app.css), a mouse
 * opens this list in the site's style instead of the system's. That is Safari and Firefox on a desktop;
 * phones and tablets keep their own picker, and Chrome and Edge style the select itself.
 *
 * The select stays the field: it keeps the focus, its label and its value, and a choice sets its value and
 * fires input and change, so Sprig, Alpine, Formie and plain form posts see a normal select. The keyboard
 * opens the system's list as before, which keeps the select as screen readers know it; this list is hidden
 * from them (aria-hidden), though the arrow keys, Enter, Escape and typing work while it is open.
 *
 * One list serves every select (a manual popover, so it sits above drawers and dialogs) and the listeners
 * are on the document: selects that Sprig or a payment gateway add later need no init. While the list is
 * open the select has data-open, for a chevron that turns (peer-data-open:rotate-180).
 */

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const checkIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
// Between the select and the list, and between the list and the window's edge
const gap = 4;
const edge = 8;

let list = null;
let current = null;
let items = [];
let activeIndex = -1;
let typed = '';
let typedTimer = null;

function shouldEnhance(select) {
    return !CSS.supports('appearance', 'base-select')
        && finePointer.matches
        && !select.multiple
        && select.size <= 1
        && !select.disabled;
}

function normalize(text) {
    return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function getList() {
    if (list) {
        return list;
    }

    list = document.createElement('div');
    list.className = 'select-list';
    list.popover = 'manual';
    list.setAttribute('aria-hidden', 'true');

    // The select keeps the focus
    list.addEventListener('mousedown', (event) => event.preventDefault());
    list.addEventListener('mousemove', (event) => {
        const index = items.findIndex((item) => item.element.contains(event.target));
        if (index !== -1 && index !== activeIndex && !items[index].option.disabled) {
            setActive(index, false);
        }
    });
    list.addEventListener('click', (event) => {
        const item = items.find((item) => item.element.contains(event.target));
        if (item && !item.option.disabled) {
            choose(item.option);
        }
    });

    document.body.append(list);

    return list;
}

function addOption(option) {
    if (option.hidden) {
        return;
    }

    const element = document.createElement('div');
    element.className = 'select-list-option';
    element.append(Object.assign(document.createElement('span'), { textContent: option.label }));
    if (option.selected) {
        element.dataset.selected = '';
        element.insertAdjacentHTML('beforeend', checkIcon);
    }
    if (option.disabled) {
        element.dataset.disabled = '';
    }

    list.append(element);
    items.push({ option, element });
}

function render(select) {
    getList().replaceChildren();
    items = [];

    for (const child of select.children) {
        if (child instanceof HTMLOptGroupElement) {
            list.append(Object.assign(document.createElement('div'), { className: 'select-list-group', textContent: child.label }));
            [...child.children].forEach(addOption);
        } else if (child instanceof HTMLOptionElement) {
            addOption(child);
        }
    }
}

// Below the select, or above it when there's more room there; in the window, as wide as the select at least
function position() {
    if (!current?.isConnected) {
        close();
        return;
    }

    const rect = current.getBoundingClientRect();
    list.style.minWidth = `${rect.width}px`;
    list.style.maxHeight = '';

    const height = list.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom - gap - edge;
    const spaceAbove = rect.top - gap - edge;
    const placeAbove = height > spaceBelow && spaceAbove > spaceBelow;
    const space = placeAbove ? spaceAbove : spaceBelow;
    if (height > space) {
        list.style.maxHeight = `${space}px`;
    }

    const width = list.offsetWidth;
    const left = rect.left + width > window.innerWidth - edge ? rect.right - width : rect.left;
    list.style.left = `${Math.max(edge, left)}px`;
    list.style.top = `${placeAbove ? rect.top - gap - Math.min(height, space) : rect.bottom + gap}px`;
}

function setActive(index, scroll = true) {
    items[activeIndex]?.element.removeAttribute('data-active');
    activeIndex = index;

    const element = items[index]?.element;
    if (!element) {
        return;
    }

    element.dataset.active = '';
    if (scroll) {
        if (element.offsetTop < list.scrollTop) {
            list.scrollTop = element.offsetTop;
        } else if (element.offsetTop + element.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = element.offsetTop + element.offsetHeight - list.clientHeight;
        }
    }
}

// The next option that can be chosen, `step` away (1 or -1); from the end with `from` -1 or items.length
function move(step, from = activeIndex) {
    for (let index = from + step; index >= 0 && index < items.length; index += step) {
        if (!items[index].option.disabled) {
            setActive(index);
            return;
        }
    }
}

// Typing jumps to the first option that starts with what was typed in the last half second
function typeAhead(key) {
    clearTimeout(typedTimer);
    typed += normalize(key);
    typedTimer = setTimeout(() => typed = '', 500);

    const index = items.findIndex((item) => !item.option.disabled && normalize(item.option.label).startsWith(typed));
    if (index !== -1) {
        setActive(index);
    }
}

function open(select) {
    current = select;
    render(select);
    list.showPopover();
    position();

    // The chosen option in the middle of the list
    const selectedIndex = items.findIndex((item) => item.option.selected && !item.option.disabled);
    const selected = items[selectedIndex]?.element;
    list.scrollTop = selected ? selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2 : 0;
    selectedIndex !== -1 ? setActive(selectedIndex, false) : move(1, -1);

    select.dataset.open = '';
    select.addEventListener('blur', close);
}

function close() {
    if (!current) {
        return;
    }

    current.removeEventListener('blur', close);
    delete current.dataset.open;
    current = null;
    activeIndex = -1;
    typed = '';

    if (list.matches(':popover-open')) {
        list.hidePopover();
    }
}

function choose(option) {
    const select = current;
    close();
    select.focus({ focusVisible: false });

    if (option.selected) {
        return;
    }

    option.selected = true;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('mousedown', (event) => {
    if (event.button !== 0) {
        return;
    }

    const select = event.target.closest?.('select');
    if (select && shouldEnhance(select)) {
        // Instead of the system's list; focused as a click would, without the keyboard's focus ring
        event.preventDefault();
        select.focus({ focusVisible: false });
        const wasOpen = current === select;
        close();
        if (!wasOpen) {
            open(select);
        }
        return;
    }

    if (current && !list.contains(event.target)) {
        close();
    }
}, true);

// While the list is open the select's keys work in it; closed, the keyboard opens the system's list
document.addEventListener('keydown', (event) => {
    if (!current || event.target !== current) {
        return;
    }

    switch (event.key) {
        case 'ArrowDown':
            move(1);
            break;
        case 'ArrowUp':
            move(-1);
            break;
        case 'Home':
        case 'PageUp':
            move(1, -1);
            break;
        case 'End':
        case 'PageDown':
            move(-1, items.length);
            break;
        case 'Enter':
            items[activeIndex] ? choose(items[activeIndex].option) : close();
            break;
        case 'Escape':
            close();
            break;
        case 'Tab':
            close();
            return;
        default:
            // A space while typing is part of the name ("Verenigd Koninkrijk"), else it chooses
            if (event.key === ' ' && !typed) {
                items[activeIndex] ? choose(items[activeIndex].option) : close();
            } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                typeAhead(event.key);
            } else {
                return;
            }
    }

    event.preventDefault();
}, true);

document.addEventListener('scroll', (event) => {
    if (current && event.target !== list) {
        position();
    }
}, { capture: true, passive: true });
window.addEventListener('resize', close);
